import React, { useEffect, useMemo, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import { FunnelIcon } from '@heroicons/react/24/solid';
import { useAssetsData } from '../../../moduleServices/assets_data_context';
import OperationCard from '../Detailed/OperationCard';
import ModalOperationEdit from '../Common/ModalOperationEdit';
import WorkflowOperationsOrderBy from './WorkflowOperationsOrderBy';
import WorkflowAssetsTable from './WorkflowAssetsTable';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const LIST_MAX_HEIGHT = 280;
const ITEM_SIZE = 64;
const LIST_MODES = [
  { value: 'assets', label: 'assets' },
  { value: 'operations', label: 'operations' },
];

const matchesNameOrCode = (item, term) => {
  const name = (item?.asset_name ?? '').toLowerCase();
  const code = (item?.asset_code ?? '').toLowerCase();
  return name.includes(term) || code.includes(term);
};

const WorkflowOperationsPanel = () => {
  const { operations, loading, error, refreshFull } = useAssetsData();
  const [listHeight, setListHeight] = useState(LIST_MAX_HEIGHT);
  const [listMode, setListMode] = useState('operations');
  const [orderBy, setOrderBy] = useState('desc');
  const [assetNameSearch, setAssetNameSearch] = useState('');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [operationToEdit, setOperationToEdit] = useState(null);

  useEffect(() => {
    const updateHeight = () => {
      const h = Math.max(120, Math.min(window.innerHeight * 0.32, LIST_MAX_HEIGHT));
      setListHeight(h);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const sortedOperations = useMemo(() => {
    const list = [...operations];
    list.sort((a, b) => {
      const da = a?.operation_date || '';
      const db = b?.operation_date || '';
      if (orderBy === 'asc') return da.localeCompare(db);
      return db.localeCompare(da);
    });
    return list;
  }, [operations, orderBy]);

  const filteredOperations = useMemo(() => {
    const term = (assetNameSearch?.trim() ?? '').toLowerCase();
    if (!term) return sortedOperations;
    return sortedOperations.filter((op) => matchesNameOrCode(op, term));
  }, [sortedOperations, assetNameSearch]);

  const hasActiveFilter = (assetNameSearch?.trim() ?? '') !== '';

  const OperationRow = ({ index, style }) => {
    const operation = filteredOperations[index];
    if (!operation) {
      return <div style={style} className="animate-pulse rounded-lg bg-gray-50 px-2 py-1.5" />;
    }
    return (
      <OperationCard
        operation={operation}
        style={style}
        onEditOperation={setOperationToEdit}
      />
    );
  };

  return (
    <>
      <div className="mb-2.5 flex items-center">
        <div
          className="inline-flex rounded-full bg-gray-100 p-0.5"
          role="group"
          aria-label="Assets or operations"
        >
          {LIST_MODES.map(({ value, label }, index) => {
            const selected = listMode === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={selected}
                onClick={() => setListMode(value)}
                className={`px-3 py-1 text-xs font-medium transition-all ${
                  index === 0 ? 'rounded-l-full' : 'rounded-r-full'
                } ${
                  selected
                    ? 'bg-green-500 text-white'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {listMode === 'assets' ? (
        <WorkflowAssetsTable />
      ) : (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/50">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="font-medium text-gray-800">Operations</span>
            {!loading && (
              <span className="text-xs text-gray-400">
                ({filteredOperations.length}
                {hasActiveFilter ? ` / ${operations.length}` : ''})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <WorkflowOperationsOrderBy value={orderBy} onChange={setOrderBy} />
            <button
              type="button"
              onClick={() => setIsFilterExpanded((f) => !f)}
              className={`rounded-lg border p-1.5 transition-colors ${
                isFilterExpanded || hasActiveFilter
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
              aria-label="Toggle filters"
            >
              <FunnelIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className={`overflow-hidden border-b border-gray-200 bg-white transition-all duration-200 ease-in-out ${
            isFilterExpanded ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex items-center px-3 py-2">
            <input
              type="search"
              value={assetNameSearch}
              onChange={(e) => setAssetNameSearch(e.target.value)}
              placeholder="Asset name or code"
              className="h-7 max-w-[220px] min-w-[140px] rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-800 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none"
              aria-label="Filter by asset name or code"
            />
          </div>
        </div>

        {error && (
          <div className="border-b border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="max-h-[32vh] bg-white">
          {loading && (
            <div className="flex justify-center py-6">
              <SpinnerLoad />
            </div>
          )}
          {!loading && operations.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">No operations recorded.</p>
          )}
          {!loading && operations.length > 0 && filteredOperations.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-400">
              No operations match the filter.
            </p>
          )}
          {!loading && filteredOperations.length > 0 && (
            <List
              height={listHeight}
              itemCount={filteredOperations.length}
              itemSize={ITEM_SIZE}
              width="100%"
              overscanCount={5}
            >
              {OperationRow}
            </List>
          )}
        </div>
      </div>
      )}

      {operationToEdit && (
        <ModalOperationEdit
          selectedOperation={operationToEdit}
          onClose={() => setOperationToEdit(null)}
          onSuccess={() => {
            setOperationToEdit(null);
            refreshFull();
          }}
        />
      )}
    </>
  );
};

export default WorkflowOperationsPanel;
